import { FC, useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { Helmet } from 'react-helmet-async'
import { enqueueSnackbar } from 'notistack'

import ProjectsTemplate from 'src/templates/dashboard/MyProjectsTemplate'
import { Project, ProjectsQueryParams, ProjectViewMode } from 'src/types/projects'
import { api } from '../../api/root'
import { ProjectType } from '../../types/type'
import { ROUTES } from '../../types/routes'
import useAuth from '../../enviroment/hooks/useAuth'
import { useChatStore } from '../../enviroment/store/chatStore'
import { useShallow } from 'zustand/react/shallow'
import { UserRole } from '../../types/user'
import { MAX_PROJECT_BULK_DELETE, MIN_SEARCH_LENGTH, SEARCH_DEBOUNCE_MS } from '../../enviroment/constants/constants'
import { useDebounceValue } from '../../enviroment/hooks/useDebounceValue'
import DeleteSelectedProjectsDialog from '../../components/organisms/dialogs/DeleteSelectedProjectsDialog'

interface ProjectsPageProps {
  mode: ProjectViewMode
}

const ProjectsPage: FC<ProjectsPageProps> = ({ mode }) => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState<string>('')
  const debouncedSearchTerm = useDebounceValue(searchTerm, SEARCH_DEBOUNCE_MS)
  const effectiveSearchTerm = debouncedSearchTerm.length >= MIN_SEARCH_LENGTH ? debouncedSearchTerm : ''
  const [selectedTypesIds, setSelectedTypesIds] = useState<string[]>([])
  const [selectedStagesIds, setSelectedStagesIds] = useState<string[]>([])
  const [selectedLocations, setSelectedLocations] = useState<string[]>([])
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const user = useAuth()
  const { upsertMessageByType, setIsExpanded } = useChatStore(
    useShallow((s) => ({ upsertMessageByType: s.upsertMessageByType, setIsExpanded: s.setIsExpanded })),
  )

  const canDeleteProjects = user.userRole === UserRole.ADMIN || user.userRole === UserRole.CONTRIBUTOR

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else if (next.size < MAX_PROJECT_BULK_DELETE) {
        next.add(id)
      } else {
        enqueueSnackbar(`You can select up to ${MAX_PROJECT_BULK_DELETE} projects at a time`, { variant: 'warning' })
      }
      return next
    })
  }, [])

  const deselectAll = useCallback(() => setSelectedIds(new Set()), [])

  const selectedIdsArray = useMemo(() => Array.from(selectedIds), [selectedIds])

  const openDeleteDialog = useCallback(() => setIsDeleteDialogOpen(true), [])

  const fetchConfig: ProjectsQueryParams = {
    includePublic: mode === 'public',
    includeTenant: mode === 'tenant',
  }

  const {
    data: typesData,
    fetchNextPage: fetchNextTypesPage,
    hasNextPage: hasNextTypesPage,
  } = api.type.projectTypes.useInfiniteQuery()
  const types = useMemo(() => typesData?.pages.flat() ?? [], [typesData])

  const { data: stages } = api.stage.projectStages.useQuery()
  const { data: locations } = api.location.projectLocations.useQuery(fetchConfig)

  const { data, isLoading, error, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    api.project.list.useInfiniteQuery({
      ...fetchConfig,
      searchTerm: effectiveSearchTerm,
      typeIds: selectedTypesIds,
      stageIds: selectedStagesIds,
      location: selectedLocations,
    })

  const handleOpenProject = (project: Project): void => {
    const redirectUrl =
      mode === 'public'
        ? ROUTES.LANDSCAPES_PROJECT_DETAILS.replace(':id', project.id)
        : ROUTES.PROJECT_DETAILS.replace(':id', project.id)

    // Keep Copa open only when the project is still processing (not yet ready) — mirrors ProjectCard's
    // "ready" check. The flag prevents the route-change collapse without force-expanding, so a closed
    // widget stays closed.
    const isProcessing = project.layerUploadStatus !== 'COMPLETED' && project.layerUploadStatus !== 'ERROR'

    navigate(redirectUrl, isProcessing ? { state: { keepWidgetOpen: true } } : undefined)
  }

  // Safely handle the data flattening with type checking
  const flattenedProjects: Project[] =
    data?.pages?.reduce<Project[]>((acc, page) => {
      if (Array.isArray(page)) {
        return [...acc, ...page]
      }
      return acc
    }, []) || []

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(flattenedProjects.slice(0, MAX_PROJECT_BULK_DELETE).map((p) => p.id)))
  }, [flattenedProjects])

  const formattedLocations = locations?.map((location) => ({
    id: location,
    name: location,
  })) as unknown as ProjectType[]

  const handleCreateProjectClick = () => {
    upsertMessageByType('create-project')
    setIsExpanded(true)
  }

  return (
    <>
      <Helmet>
        <title>{mode === 'public' ? 'RAMO - Landscapes' : 'RAMO - My Projects'}</title>
      </Helmet>

      <ProjectsTemplate
        mode={mode}
        projects={flattenedProjects}
        isLoading={isLoading}
        isFetching={isFetching}
        fetchNextProjectsPage={fetchNextPage}
        hasMoreProjects={hasNextPage}
        isFetchingMoreProjects={isFetchingNextPage}
        searchTerm={searchTerm}
        selectedTypesIds={selectedTypesIds}
        types={types}
        onTypesScrollEnd={() => hasNextTypesPage && fetchNextTypesPage()}
        stages={stages}
        locations={formattedLocations}
        selectedLocations={selectedLocations}
        setSelectedLocations={setSelectedLocations}
        setSelectedTypesIds={setSelectedTypesIds}
        selectedStagesIds={selectedStagesIds}
        setSelectedStagesIds={setSelectedStagesIds}
        setSearchTerm={(e) => setSearchTerm(e.target.value)}
        error={error}
        onOpenProject={handleOpenProject}
        role={user.userRole}
        onCreateProjectClick={handleCreateProjectClick}
        selectedIds={selectedIds}
        onToggleSelect={mode === 'tenant' && canDeleteProjects ? toggleSelect : undefined}
        onSelectAll={mode === 'tenant' ? selectAll : undefined}
        onDeselectAll={mode === 'tenant' ? deselectAll : undefined}
        onDeleteSelected={mode === 'tenant' ? openDeleteDialog : undefined}
      />

      <DeleteSelectedProjectsDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        projectIds={selectedIdsArray}
        singleProjectName={
          selectedIdsArray.length === 1 ? flattenedProjects.find((p) => p.id === selectedIdsArray[0])?.name : undefined
        }
        onDeleted={deselectAll}
      />
    </>
  )
}

export default ProjectsPage
